import { createSlice } from '@reduxjs/toolkit';
import { DiscountTargets } from '../../pages/couponManage/constants';

export const CouponSelectionModes = {
  NONE: 'none',
  SHOP_COUPON: 'shop_coupon',
  USER_COUPON: 'user_coupon'
};

// A held coupon travels as its UserCoupon id, a typed shop coupon as its code only.
export const selectCouponRequestParams = shoppingCart => {
  const slotParams = (coupon, mode) => ({
    userCouponId: mode === CouponSelectionModes.USER_COUPON ? coupon?._id || null : null,
    couponCode: mode === CouponSelectionModes.SHOP_COUPON ? coupon?.code || null : null
  });
  const item = slotParams(shoppingCart.selectedCoupon, shoppingCart.couponSelectionMode);
  const shipping = slotParams(shoppingCart.selectedShippingCoupon, shoppingCart.shippingCouponSelectionMode);
  return {
    ...item,
    shippingUserCouponId: shipping.userCouponId,
    shippingCouponCode: shipping.couponCode
  };
};

export const initialPaymentState = {
  point: {
    checked: false,
    amount: 0,
    convertedAmount: 0
  },
  credit: {
    checked: false,
    amount: 0,
    convertedAmount: 0
  },
  qrcode: {
    checked: false
  },
  cod: {
    checked: false
  },
  rawCash: {
    checked: false
  },
  mobileBanking: {
    checked: false
  },
  globalCard: {
    checked: false
  },
  qr2C2P: {
    checked: false
  },
  allNewebpay: {
    checked: false
  },
  dragonpayCreditCard: {
    checked: false
  },
  dragonpayEWallet: {
    checked: false
  },
  dragonpayOnlineBanking: {
    checked: false
  },
  linktopay: {
    checked: false
  },
  onlineMonster: {
    checked: false
  }
};

// full-form tax invoice request, sent as `taxInvoice` when the order is placed
const initialTaxInvoice = { isRequested: false, companyName: '', taxId: '', address: '' };

const initialCartState = {
  cartId: null,
  targetCustomer: null,
  items: [],
  payment: initialPaymentState,
  couponSelectionMode: CouponSelectionModes.NONE,
  selectedCoupon: null,
  shippingCouponSelectionMode: CouponSelectionModes.NONE,
  selectedShippingCoupon: null,
  conversionRateList: {},
  remark: '',
  taxInvoice: initialTaxInvoice,
  shoppingCartSetting: {
    VAT: {
      isEnabled: false,
      percentage: 0,
      mode: 'included'
    },
    serviceCharge: {
      isEnabled: false,
      percentage: 0,
      mode: 'included'
    }
  }
};

export const ShoppingCartSlice = createSlice({
  name: 'shoppingCart',
  initialState: initialCartState,
  reducers: {
    setTargetCustomerData: (state, action) => {
      if ('targetCustomer' in action.payload) state.targetCustomer = action.payload.targetCustomer;
      if ('targetBranch' in action.payload) state.targetBranch = action.payload.targetBranch;
    },
    updateShoppingCart: (state, action) => {
      state.items = action.payload.items;
      if (state.cartId !== undefined) {
        state.cartId = action.payload.cartId;
      }
    },
    updatePaymentMethod: (state, action) => {
      state.paymentMethod = action.payload.paymentMethod;
    },
    setConversionRateList: (state, action) => {
      state.conversionRateList = action.payload;
    },
    setCurrentCoupon: (state, { payload }) => {
      const mode = payload.coupon ? payload.couponSelectionMode : CouponSelectionModes.NONE;
      if (payload.target === DiscountTargets.SHIPPING) {
        state.selectedShippingCoupon = payload.coupon;
        state.shippingCouponSelectionMode = mode;
      } else {
        state.selectedCoupon = payload.coupon;
        state.couponSelectionMode = mode;
      }
    },
    clearCoupons: state => {
      state.selectedCoupon = null;
      state.couponSelectionMode = CouponSelectionModes.NONE;
      state.selectedShippingCoupon = null;
      state.shippingCouponSelectionMode = CouponSelectionModes.NONE;
    },
    setPayment: (state, action) => {
      state.payment = action.payload;
    },
    clearPayment: state => {
      state.payment = initialPaymentState;
    },
    setRemark: (state, action) => {
      state.remark = action.payload;
    },
    setTaxInvoice: (state, action) => {
      state.taxInvoice = { ...state.taxInvoice, ...action.payload };
    },
    clearTaxInvoice: state => {
      state.taxInvoice = initialTaxInvoice;
    },
    updateShoppingCartSetting: (state, action) => {
      state.shoppingCartSetting = action.payload;
    },
    catchError: (state, action) => {
      state.error = action.payload;
    }
  }
});
